// File: `typescript/apps/front-end/src/main/react/list/ThoughtInDiscussionListDisplayer.tsx`
import React from "react";
import {Localizer} from "@/main/react/Types";
import {GroupDTO} from "@/generated-client";
import {PaginationType} from "@/main/common/PaginationType";
import {ClientFactory} from "@/main/common/ClientFactory";
import ThoughtInDiscussionCardDisplayer from "@/main/react/thoughts/ThoughtInDiscussionCardDisplayer";
import InsertedItemMotion from "@/main/react/component/InsertedItemMotion";
import { PaginatedListWrapperV2 } from "@/main/react/list/PaginatedListWrapperV2";
import { createStandardPaginatedStatusSlots, PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { MessageSquareText, SquarePen } from "lucide-react";
import {
    buildDiscussionOpeningArgumentRequest,
    DiscussionOpeningThoughtOrderingStyle,
    normalizeDiscussionGroupPublicIds,
    OpeningThoughtWithGroupMatchesDTO,
} from "@/main/react/discussion/OpeningThoughtGroupTypes";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import type { QuestionStateView } from "@/main/common/QuestionFacetTypes";
import {unwrapAxiosData} from "@/main/common/ApiUtils";
import { cn } from "@/lib/utils";
import { ThreadShortcutsHelpDialog, useThreadKeyboardShortcuts } from "@/main/react/thoughts/ThreadKeyboardShortcuts";
import { findFirstNewThoughtPublicId, JumpToNewRepliesButton, NewSinceLastVisitDivider } from "@/main/react/discussion/NewSinceLastVisit";

function localize(localizer: Localizer, key: LocalizationKey, ...args: (string | number)[]): string {
    return localizer(key, ...args);
}

export type Props = {
    discussionSlug: string;
    // Host post title, for the share payload only (BL-083) — nothing in this list renders it. Optional
    // and pass-through the whole way down; an un-threaded mount keeps the pre-2026-08-07 payload shape.
    discussionTitle?: string;
    loggedInUsername?: string;
    discussionPublicId: string;
    paginationType?: PaginationType | string;
    openingArguments?: OpeningThoughtWithGroupMatchesDTO[];
    agreementAgreementTitle?: string;
    disagreementAgreementTitle?: string;
    localizer: Localizer;
    isSensitive?: boolean;
    suppressSpoilerGuards?: boolean;
    onLoadMore?: () => void;
    loading?: boolean;
    hasMore?: boolean;
    initialViewerDiscussionAgreement: any;

    onDiscussClicked?: (argumentPublicId: string) => void;
    onWriteArgumentClicked?: () => void;
    /**
     * Set by the owner exactly when the top-level composer (which renders the stance chips itself) is
     * live on the page. Since U4 (2026-09-28) its presence is what tells this list that the composer
     * OWNS the zero-reply on-ramp, so the empty panel renders its title only; the list no longer renders
     * chips of its own and never calls it. Round 25 `D41` added the panel's chips; U4 reversed that.
     */
    onStancePromptChosen?: (prefill: string) => void;
    onViewConversationClicked?: (conversationPublicId: string, argumentPublicId?: string) => void;
    activeConversationPublicId?: string | null;

    excludeOpeningArgumentPublicIds?: string[];
    discussionGroupsByPublicId?: Record<string, GroupDTO>;
    selectedGroupPublicIds?: string[];
    openingArgumentOrdering?: DiscussionOpeningThoughtOrderingStyle;
    onClearGroupFilter?: () => void;
    newlyPublishedOpeningArgumentPublicIds?: string[];
    // Editorial pin: fired after a successful pin/unpin so the owner can re-fetch page 1 and re-sort.
    // The pinned thought itself is read out of the LOADED list below rather than passed in, because
    // this component owns the paged items and the pin is always first on page 1.
    onPinChanged?: () => void;
    // QUESTION facet: reframes the empty/end states and the write CTA as answers, and carries an
    // accept/unaccept's QuestionStateView up so the owner can re-fetch page 1 (the accepted answer
    // floats to the top under every ordering, exactly as a pin does).
    isQuestion?: boolean;
    onAcceptChanged?: (view: QuestionStateView) => void;
    /** The POST author's username, for the "This changed {0}'s mind" badge. Forwarded, never derived. */
    discussionAuthorUsername?: string;
    /**
     * Round 37 `2a` (FI-583): the viewer's PREVIOUS visit (ISO instant), null/absent for anonymous or a first
     * visit. Draws "Jump to new replies" above the list and the "New since your last visit" divider above the
     * first top-level thought written after it (see `NewSinceLastVisit.tsx`).
     */
    viewerLastVisitedAt?: string | null;
};

export default function ThoughtInDiscussionListDisplayer(props: Props) {
    const {
        discussionSlug ,
        discussionTitle,
        loggedInUsername,
        // NO default. `undefined` is a signal, not a missing value: it is how the owner
        // (`DiscussionThoughtsDisplayer`) hands the list back its own fetch path after an interrupted
        // or failed initial load. `PaginatedListWrapperV2` branches on `== null`, and `[] == null` is
        // false — so a `= []` default here landed that recovery on the SEEDED branch, issued no
        // request, and rendered "No thoughts yet" on a discussion that has thoughts (fixed 2026-08-11).
        // Pinned by ThoughtInDiscussionListDisplayer.spec.tsx.
        openingArguments,
        agreementAgreementTitle = "",
        disagreementAgreementTitle = "",
        localizer,
        discussionPublicId,
        isSensitive = false,
        suppressSpoilerGuards = false,
        onWriteArgumentClicked,
        onStancePromptChosen,
        onViewConversationClicked,
        activeConversationPublicId,
        excludeOpeningArgumentPublicIds = [],
        discussionGroupsByPublicId = {},
        selectedGroupPublicIds = [],
        openingArgumentOrdering = "new",
        onClearGroupFilter,
        newlyPublishedOpeningArgumentPublicIds = [],
        onPinChanged,
        isQuestion = false,
        onAcceptChanged,
        discussionAuthorUsername,
        viewerLastVisitedAt,
    } = props;

    const nativeResumeNewReplies = React.useMemo(
        // FI-589: a thread left open in a web tab offers the replies that arrived meanwhile, too.
        () => ({ newItemsLabel: localizer("feed.resume.new_replies"), onWebTabReturn: true }),
        [localizer],
    );

    // FI-586: j/k/r/? over the opening threads this list renders.
    const threadListRef = React.useRef<HTMLDivElement | null>(null);
    const { helpOpen: shortcutsHelpOpen, setHelpOpen: setShortcutsHelpOpen } = useThreadKeyboardShortcuts(threadListRef);

    const excludeSet = new Set((excludeOpeningArgumentPublicIds || []).filter(Boolean));
    const normalizedSelectedGroupPublicIds = React.useMemo(
        () => normalizeDiscussionGroupPublicIds(selectedGroupPublicIds),
        [selectedGroupPublicIds],
    );
    const isGroupFilterActive = normalizedSelectedGroupPublicIds.length > 0;
    const newlyPublishedOpeningArgumentIdSet = React.useMemo(
        () => new Set((newlyPublishedOpeningArgumentPublicIds ?? []).filter(Boolean)),
        [newlyPublishedOpeningArgumentPublicIds],
    );
    const request = React.useMemo(
        () => buildDiscussionOpeningArgumentRequest(normalizedSelectedGroupPublicIds, openingArgumentOrdering),
        [normalizedSelectedGroupPublicIds, openingArgumentOrdering],
    );

    const fetchPage = async (paginationParameter?: string): Promise<OpeningThoughtWithGroupMatchesDTO[]> => {
        const data = await unwrapAxiosData<OpeningThoughtWithGroupMatchesDTO[] | undefined>(
            ClientFactory.listDiscussionOpeningThoughts<OpeningThoughtWithGroupMatchesDTO[]>({
                discussionPublicId,
                paginationParameter,
                groupPublicIds: request.groupPublicIds,
                openingArgumentOrdering: request.openingArgumentOrdering,
            }),
        );
        const nextItems = Array.isArray(data) ? data : [];
        return nextItems.map((r) => Object.assign({}, r));
    };

    const getId = (item: OpeningThoughtWithGroupMatchesDTO) => item.openingArgument?.publicId;

    const visibleItemsCount = React.useCallback((items: OpeningThoughtWithGroupMatchesDTO[]) => {
        return items.filter(item => {
            const id = item?.openingArgument?.publicId;
            return !!id && !excludeSet.has(id);
        }).length;
    }, [excludeSet]);

    const writeArgumentAction = onWriteArgumentClicked
        ? {
            label: localizer(isQuestion ? "discussion.question.answers.write" : "discussion.floating.write_thought"),
            onClick: onWriteArgumentClicked,
            icon: SquarePen,
        }
        : undefined;

    // The zero-answer state — design §11.1 calls it the highest-leverage empty state in the product, and
    // it is the one place a reader decides whether to answer at all. The group-filter branch keeps its own
    // copy in both shapes: "no thoughts match this filter" is a statement about the FILTER, not about the
    // question, and swapping it for "No answers yet" would assert something false.
    const emptyStateTitle = isGroupFilterActive
        ? localize(localizer, "discussion.thoughts.filter.groups.empty.title")
        : localizer(isQuestion ? "discussion.question.empty.title" : "discussion.thoughts.empty.title");
    const emptyStateDescription = isGroupFilterActive
        ? localize(localizer, "discussion.thoughts.filter.groups.empty.description")
        : localizer(isQuestion ? "discussion.question.empty.description" : "discussion.thoughts.empty.description");
    // SC-2a (second-chance design §4 SC5 change 1): a reader who has just found a post with no replies is
    // the person most likely to answer another one, so the zero-reply panel carries a quiet route into
    // the unanswered lane. The lane PAGE is the home feed's transient `unanswered` lane (HomeController
    // reads `feed`); a plain anchor keeps history out of it (rule 33). It rides the panel's `secondary`
    // slot, never a second button: the write CTA stays the one action. Withheld on the group-filter
    // branch, where the panel describes the FILTER rather than the post. A soft link: the home route
    // reads the transient lane off the URL (`ClientRouteRegistry.normalizeHomeFeedType`, FI-022).
    const unansweredLaneLink = isGroupFilterActive
        ? undefined
        : (
            <a
                href="/?feed=unanswered"
                data-playwright-id="discussion-unanswered-more-link"
                // The same recipe as `GuideLink`, the panel's other `secondary` occupant, so the two
                // quiet lines read as one dialect: muted, underlined, a coarse-pointer tap floor
                // (`EMPTY-STATES-74`), one hover wash, one focus ring.
                className={cn(
                    "inline-flex items-center text-sm text-muted-foreground! underline underline-offset-4",
                    "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                    "standard-hover rounded-sm outline-none",
                    "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                )}
            >
                {localize(localizer, "discussion.unanswered.more_link")}
            </a>
        );

    // U4 (S7 ux-critique settle, 2026-09-28; BOARD-5, BOARD-22): the COMPOSER owns the zero-reply
    // on-ramp. Round 25 `D41` gave this panel the same three stance chips the inline composer directly
    // above it already renders on every writable post, plus a description restating the composer's own
    // invitation, so one phone screen asked the same question twice and said "be the first" three times.
    // Where the composer is there to own it (the caller hands us `onStancePromptChosen` exactly when the
    // top-level composer is live, and neither a question nor a group filter applies), the panel is its
    // title: no description, no chips, no write action. It keeps the SC-2a unanswered-lane link, which
    // is a route elsewhere rather than a repeat of the ask. A question and the group-filter panel keep
    // their own copy and action.
    const composerOwnsZeroReplyPrompt = !isGroupFilterActive && !isQuestion && Boolean(onWriteArgumentClicked && onStancePromptChosen);

    return (
        <>
        <ThreadShortcutsHelpDialog open={shortcutsHelpOpen} onOpenChange={setShortcutsHelpOpen} localizer={localizer} />
        <PaginatedListWrapperV2<OpeningThoughtWithGroupMatchesDTO>
            fetchPage={fetchPage}
            // Native resume (round 12 slice 1m, D11/D28): silent swap at the top, "New replies" pill below.
            nativeResumeRefresh={nativeResumeNewReplies}
            getId={getId}
            initialItems={openingArguments}
            getVisibleItemsCount={visibleItemsCount}
            statusSlots={createStandardPaginatedStatusSlots({
                loadingMessage: localizer("home.loading"),
                errorMessage: localizer("paginated.load_failed"),
                retryLabel: localizer("literal.retry"),
                empty: (
                    composerOwnsZeroReplyPrompt ? (
                        // The SC-2a route stays: it is a way OUT to other unanswered posts, not a second
                        // ask to reply here, and `unanswered-lane-route.spec.mjs` pins it on purpose.
                        <PaginatedListEmptyState
                            icon={MessageSquareText}
                            title={emptyStateTitle}
                            secondary={unansweredLaneLink}
                        />
                    ) : (
                        <PaginatedListEmptyState
                            icon={MessageSquareText}
                            title={emptyStateTitle}
                            description={emptyStateDescription}
                            action={isGroupFilterActive && onClearGroupFilter
                                ? {
                                    label: localize(localizer, "discussion.thoughts.filter.groups.empty.clear"),
                                    onClick: onClearGroupFilter,
                                    icon: MessageSquareText,
                                }
                                : writeArgumentAction}
                            secondary={unansweredLaneLink}
                        />
                    )
                ),
                end: (
                    <PaginatedListEmptyState
                        icon={MessageSquareText}
                        title={localizer("discussion.thoughts.end.title")}
                        description={localizer("discussion.thoughts.end.description")}
                        action={writeArgumentAction}
                    />
                ),
            })}
            render={({ items }) => {
                // Which thought currently holds the discussion's single pin slot. Read off the LOADED
                // list — reliable because the server returns the pinned thought first on page 1 and this
                // list always holds page 1. If it were ever wrong the failure is a missing confirmation
                // dialog, not a wrong write: the server replaces the pin either way.
                const pinnedThoughtPublicId = items
                    .map((item) => item?.openingArgument)
                    .find((thought) => Boolean((thought as { pinned?: boolean | null } | undefined)?.pinned))
                    ?.publicId ?? null;
                const firstNewThoughtPublicId = findFirstNewThoughtPublicId(
                    items, viewerLastVisitedAt, loggedInUsername, excludeSet);
                return (
                    <>
                    {firstNewThoughtPublicId ? <JumpToNewRepliesButton localizer={localizer} /> : null}
                    <div className="argument-list-container " ref={threadListRef}>
                        {items.map((openingArgument) => {
                            const openingId = openingArgument.openingArgument?.publicId;
                            if (!openingId) return null;
                            if (excludeSet.has(openingId)) return null;
                            const isNewlyPublished = newlyPublishedOpeningArgumentIdSet.has(openingId);
                            return (
                                <React.Fragment key={openingId}>
                                {openingId === firstNewThoughtPublicId ? <NewSinceLastVisitDivider localizer={localizer} /> : null}
                                <InsertedItemMotion
                                    key={openingId}
                                    active={isNewlyPublished}
                                    playwrightId={isNewlyPublished ? `inserted-opening-thought-${openingId}` : undefined}
                                >
                                    <ThoughtInDiscussionCardDisplayer
                                        openingArgument={openingArgument}
                                        loggedInUsername={loggedInUsername}
                                        discussionSlug={discussionSlug}
                                        discussionTitle={discussionTitle}
                                        agreementAgreementTitle={agreementAgreementTitle}
                                        disagreementAgreementTitle={disagreementAgreementTitle}
                                        localizer={localizer}
                                        discussionPublicId={discussionPublicId}
                                        isSensitive={isSensitive}
                                        suppressSpoilerGuards={suppressSpoilerGuards}
                                        discussionGroupsByPublicId={discussionGroupsByPublicId}
                                        selectedGroupPublicIds={normalizedSelectedGroupPublicIds}
                                        onViewConversationClicked={onViewConversationClicked}
                                        activeConversationPublicId={activeConversationPublicId}
                                        pinnedThoughtPublicId={pinnedThoughtPublicId}
                                        onPinChanged={onPinChanged}
                                        isQuestion={isQuestion}
                                        onAcceptChanged={onAcceptChanged}
                                        discussionAuthorUsername={discussionAuthorUsername}
                                    />
                                </InsertedItemMotion>
                                </React.Fragment>
                            );
                        })}
                    </div>
                    </>
                );
            }}
        />
        </>
    );
}
